import React, { useState } from 'react';
import {
  Camera,
  QrCode,
  UserCheck,
  Megaphone,
  BarChart3,
  Star,
  MessageSquare,
  Building2,
  ChevronRight,
} from 'lucide-react';

const FEATURES = [
  {
    icon: Camera,
    title: 'Vehicle ANPR',
    desc: 'Live number-plate recognition feeds straight from the gate camera. GREEN = approved resident, RED = blacklisted, instant resident notification on every check-in.',
    img: '/landing/feature-anpr.jpg',
  },
  {
    icon: QrCode,
    title: 'Gate Passes & QR codes',
    desc: 'Pre-approve guests in seconds. Share a QR or 4/6-digit access code over WhatsApp — guards just scan and let them in.',
    img: '/landing/feature-gate-passes.jpg',
  },
  {
    icon: UserCheck,
    title: 'Visitor Approvals',
    desc: 'Walk-in visitors get sent to your phone. One tap to approve, deny, or hold. Auto-logged, timestamped, audit-ready.',
    img: '/landing/feature-visitor-approvals.jpg',
  },
  {
    icon: Megaphone,
    title: 'Notice Board',
    desc: 'Targeted notices with read-receipts, priority pinning, and email digests. No more lost WhatsApp messages.',
    img: '/landing/feature-notice-board.jpg',
  },
  {
    icon: BarChart3,
    title: 'Surveys & Polls',
    desc: 'Run polls with explicit expiry, post-vote stats and a Trending Now card on every resident dashboard. Get real participation.',
    img: '/landing/feature-notice-board.jpg',
  },
  {
    icon: Star,
    title: 'Recommendations',
    desc: 'Crowd-sourced trusted contacts — plumbers, tutors, cooks — organized by category. Find what your neighbors love.',
    img: '/landing/audience-resident.jpg',
  },
  {
    icon: MessageSquare,
    title: 'Resident Chat',
    desc: 'Group chats that auto-purge after 24 hours, by design. Quick coordination without the noise of forever-history.',
    img: '/landing/audience-resident.jpg',
  },
  {
    icon: Building2,
    title: 'Multi-tenant Communities',
    desc: 'Run a single society or a portfolio. Strict per-community isolation, multi-community membership for residents who own across sites.',
    img: '/landing/feature-multi-tenant.jpg',
  },
];

export const FeatureSuite = () => {
  const [active, setActive] = useState(0);
  const Cur = FEATURES[active];

  return (
    <section
      id="features"
      className="py-24 lg:py-32 bg-[#0F172A] text-white relative overflow-hidden"
      data-testid="feature-suite-section"
    >
      {/* radial glow */}
      <div className="absolute top-1/3 -left-32 w-[500px] h-[500px] bg-[#14D4C6]/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-blue-500/5 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        <div className="max-w-2xl">
          <p
            className="text-base md:text-lg tracking-wide uppercase text-[#14D4C6] font-semibold mb-3"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            The full feature suite
          </p>
          <h3
            className="text-3xl sm:text-4xl lg:text-5xl tracking-tight font-bold leading-tight"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="feature-suite-headline"
          >
            One app. Every gate-life moment.
          </h3>
          <p
            className="mt-5 text-slate-400 text-base leading-relaxed"
            style={{ fontFamily: 'Work Sans, sans-serif' }}
          >
            Click any feature to see it live in the product.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Feature list */}
          <div className="lg:col-span-5 space-y-2">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              const isActive = i === active;
              return (
                <button
                  key={f.title}
                  onClick={() => setActive(i)}
                  className={`w-full text-left p-4 rounded-xl border transition-all flex items-start gap-4 group ${
                    isActive
                      ? 'bg-white/[0.07] border-[#14D4C6]/40 shadow-[0_0_0_1px_rgba(20,212,198,0.2)]'
                      : 'bg-transparent border-white/5 hover:bg-white/[0.03] hover:border-white/10'
                  }`}
                  data-testid={`feature-switcher-${i}`}
                >
                  <div
                    className={`w-10 h-10 shrink-0 rounded-lg flex items-center justify-center transition-colors ${
                      isActive
                        ? 'bg-[#14D4C6] text-slate-900'
                        : 'bg-white/5 text-slate-300 group-hover:bg-white/10'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h4
                        className="text-xl font-semibold tracking-tight"
                        style={{ fontFamily: 'Outfit, sans-serif' }}
                      >
                        {f.title}
                      </h4>
                      <ChevronRight
                        className={`w-4 h-4 transition-all ${
                          isActive
                            ? 'text-[#14D4C6] translate-x-0.5'
                            : 'text-slate-600'
                        }`}
                      />
                    </div>
                    {isActive && (
                      <p
                        className="mt-2 text-sm text-slate-300 leading-relaxed"
                        style={{ fontFamily: 'Work Sans, sans-serif' }}
                        data-testid={`feature-desc-${i}`}
                      >
                        {f.desc}
                      </p>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Sticky preview */}
          <div className="lg:col-span-7">
            <div className="lg:sticky lg:top-24">
              <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-[#0B1120] shadow-2xl">
                <div className="px-4 py-3 flex items-center gap-2 bg-white/[0.03] border-b border-white/5">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
                  <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/70" />
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-500/70" />
                  <span className="ml-3 text-xs font-mono text-slate-400 truncate">
                    gatekeeper.homes/{Cur.title.toLowerCase().replace(/\s/g, '-')}
                  </span>
                </div>
                <div className="relative aspect-[16/10] bg-slate-900">
                  {FEATURES.map((f, i) => (
                    <img
                      key={f.title}
                      src={f.img}
                      alt={f.title}
                      className={`absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-500 ${
                        i === active ? 'opacity-100' : 'opacity-0'
                      }`}
                      data-testid={`feature-preview-img-${i}`}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
